import React, { useEffect, useState, memo, useCallback } from 'react';
import { Button, Typography, Spin, Alert, Tooltip, message } from 'antd';
import { CopyOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { type VehicleSelectionValue } from '@/shared/components/VehicleEnrichment';
import { 
  usePostApiGuidesSearch, 
  usePostApiGuideviewsViews,
  type SearchClientResponse, 
  type SearchClientRequest
} from '@/features/guides/api/generated';
import { SearchResultCard } from './SearchResultCard';
import { SearchActions } from './SearchActions';
import { logger } from '@/shared/utils/logger';
import { mapI18nLanguageToLocale } from '@/shared/config/localeConfig';

const { Title, Paragraph, Text } = Typography;

interface SearchResultsProps {
  problemDescription: string;
  vehicleData: VehicleSelectionValue;
  results: SearchClientResponse | null;
  onSearchComplete: (results: SearchClientResponse) => void;
  onBack: () => void;
  onStartOver: () => void;
}

export const SearchResults: React.FC<SearchResultsProps> = memo(({
  problemDescription,
  vehicleData,
  results,
  onSearchComplete,
  onBack,
  onStartOver
}) => {
  const [hasSearched, setHasSearched] = useState(false);
  const [currentResultIndex, setCurrentResultIndex] = useState(0);
  const [viewedGuides, setViewedGuides] = useState<Map<string, string>>(new Map());
  const [guidesWithFeedback, setGuidesWithFeedback] = useState<Map<string, boolean>>(new Map());
  const [searchLocale, setSearchLocale] = useState<string | null>(null);
  const { t, i18n } = useTranslation();
  const createViewMutation = usePostApiGuideviewsViews();

  const searchMutation = usePostApiGuidesSearch({
    mutation: {
      onSuccess: (data) => {
        logger.debug('Search successful', data);
        onSearchComplete(data);
      },
      onError: (error) => {
        logger.error('Search failed', error);
      }
    }
  });

  const currentResult = results?.results?.[currentResultIndex];
  const totalResults = results?.results?.length || 0;

  const isDemo = import.meta.env.VITE_APP_MODE === 'demo';

  const trackGuideView = useCallback((guideId: string, requestId: string, forceTrack: boolean = false) => {
    // Track views in demo mode, or when feedback is clicked in admin mode
    if (!isDemo && !forceTrack) return;
    
    if (!guideId || !requestId || viewedGuides.has(guideId)) {
      return; // Already tracked this guide or missing data
    }

    logger.debug('Tracking guide view', { guideId, requestId });
    
    createViewMutation.mutate(
      {
        data: {
          guideId: guideId,
          requestId: requestId
        }
      },
      {
        onSuccess: (response) => {
          if (response.id) {
            setViewedGuides(prev => new Map(prev).set(guideId, response.id!));
            logger.debug('Guide view tracked successfully', { guideId, viewId: response.id });
          }
        },
        onError: (error) => {
          logger.error('Failed to track guide view', error);
          // Set a temporary ID so feedback buttons still work
          setViewedGuides(prev => new Map(prev).set(guideId, 'error-' + Date.now()));
        }
      }
    );
  }, [isDemo, createViewMutation]); // Removed viewedGuides from dependencies to avoid infinite loop

  useEffect(() => {
    if (currentResult && results?.requestId) {
      // Use guideId from the search result (not guide.id)
      const guideId = currentResult.guideId || currentResult.guide?.id;
      const requestId = results.requestId;
      
      if (guideId && typeof guideId === 'string' && requestId) {
        // Add 1 second delay to ensure backend has processed the search result
        const timer = setTimeout(() => {
          trackGuideView(guideId, requestId);
        }, 1000);
        
        return () => clearTimeout(timer);
      }
    }
  }, [currentResultIndex, results?.requestId]); // Track when results arrive or index changes

  const handlePrevious = () => {
    if (currentResultIndex > 0) {
      setCurrentResultIndex(currentResultIndex - 1);
    }
  };

  const handleNext = () => {
    if (currentResultIndex < totalResults - 1) {
      setCurrentResultIndex(currentResultIndex + 1);
    }
  };

  useEffect(() => {
    // Only trigger search once when we have data and haven't searched yet
    if (!results && !hasSearched && !searchMutation.isPending && problemDescription && vehicleData.manufacturer) {
      logger.debug('Triggering search', { problemDescription, vehicleData });

      // Use current UI language mapped to full locale
      const locale = mapI18nLanguageToLocale(i18n.language);

      // Save the locale used for this search
      setSearchLocale(locale);

      const searchRequest: SearchClientRequest = {
        locale,
        problem: problemDescription,
        vehicle: {
          manufacturer: vehicleData.manufacturer || null,
          modelSeries: vehicleData.modelSeries || null,
          engineBaseType: vehicleData.engineBaseType || null,
          modelYear: vehicleData.modelYear || null,
          euroCode: vehicleData.euroCode || null,
        },
        page: 1,
        pageSize: 5
      };

      setHasSearched(true);
      searchMutation.mutate({ data: searchRequest });
    }
  }, [problemDescription, vehicleData, results, hasSearched, searchMutation, i18n.language]);

  logger.debug('SearchResults state', {
    isPending: searchMutation.isPending,
    isError: searchMutation.isError,
    hasSearched,
    hasResults: !!results
  });

  if (searchMutation.isPending || (!results && hasSearched && !searchMutation.isError)) {
    return (
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        flex: 1,
        minHeight: '100%'
      }}>
        <div style={{ textAlign: 'center', padding: '48px 24px' }}>
          <Spin size="large" />
          <Title level={4} style={{ marginTop: 24, marginBottom: 16 }}>
            {t('demo.searchResults.searching')}
          </Title>
          <Paragraph style={{ color: '#666', fontSize: 16 }}>
            {t('demo.searchResults.searchingSubtitle')}
          </Paragraph>
          <div style={{ marginTop: 32 }}>
            <Button onClick={onBack}>
              {t('demo.searchResults.cancelSearch')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (searchMutation.isError) {
    return (
      <div style={{ textAlign: 'center' }}>
        <Title level={4} style={{ color: '#ff4d4f', marginBottom: 16 }}>
          {t('demo.searchResults.searchFailed')}
        </Title>
        <Alert
          message={t('demo.searchResults.searchFailedMessage')}
          description={t('demo.searchResults.searchFailedDescription')}
          type="error"
          showIcon
          style={{ marginBottom: 24 }}
        />
        <SearchActions
          onBack={onBack}
          onStartOver={onStartOver}
          onRetry={() => {
            setHasSearched(false);
            searchMutation.reset();
          }}
          showRetry={true}
          variant="error"
        />
      </div>
    );
  }

  if (!currentResult) {
    return (
      <div style={{ textAlign: 'center' }}>
        <Title level={4} style={{ marginBottom: 16 }}>
          {t('demo.searchResults.noGuides')}
        </Title>
        <Alert
          message={t('demo.searchResults.noGuidesMessage')}
          description={t('demo.searchResults.noGuidesDescription')}
          type="warning"
          showIcon
          style={{ marginBottom: 24 }}
        />
        <SearchActions
          onBack={onBack}
          onStartOver={onStartOver}
        />
      </div>
    );
  }

  const currentGuideId = currentResult?.guideId || currentResult?.guide?.id;
  const feedbackType = currentGuideId ? guidesWithFeedback.get(currentGuideId) : undefined;

  const handleFeedbackSolved = () => {
    if (currentGuideId) {
      setGuidesWithFeedback(prev => new Map(prev).set(currentGuideId, true));
    }
    // In admin mode, track view when feedback is clicked
    if (!isDemo && currentResult && results?.requestId) {
      const guideId = currentResult.guideId || currentResult.guide?.id;
      if (guideId && typeof guideId === 'string' && results.requestId) {
        trackGuideView(guideId, results.requestId, true);
      }
    }
  };

  const handleFeedbackNotSolved = () => {
    if (currentGuideId) {
      setGuidesWithFeedback(prev => new Map(prev).set(currentGuideId, false));
    }
  };

  const handleNavigateToNextGuide = () => {
    // Navigate to next guide after state has been updated
    if (currentResultIndex < totalResults - 1) {
      setTimeout(() => {
        setCurrentResultIndex(currentResultIndex + 1);
      }, 600);
    }
  };

  const handleCopyGuideId = () => {
    if (currentGuideId) {
      navigator.clipboard.writeText(currentGuideId).then(() => {
        message.success(t('demo.searchResults.guideIdCopied'));
      }).catch((error) => {
        logger.error('Failed to copy guide ID', error);
        message.error('Failed to copy guide ID');
      });
    }
  };

  const truncatedGuideId = currentGuideId ? (currentGuideId.length > 12 ? `${currentGuideId.substring(0, 12)}...` : currentGuideId) : null;

  return (
    <div>
      <SearchResultCard
        result={currentResult}
        searchVehicle={vehicleData}
        requestedLocale={searchLocale || undefined}
        feedbackType={feedbackType}
        guideViewId={(() => {
          const guideId = currentResult?.guideId || currentResult?.guide?.id;
          return guideId ? viewedGuides.get(guideId) : undefined;
        })()}
        onFeedbackSolved={handleFeedbackSolved}
        onFeedbackNotSolved={handleFeedbackNotSolved}
        onNavigateToNext={handleNavigateToNextGuide}
      />
      
      {/* Navigation between results */}
      {totalResults > 1 && (
        <div style={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          marginBottom: 0,
          gap: 8
        }}>
          <div
            className="nav-container"
            style={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              width: '100%',
              minHeight: 32
            }}
          >
            <div className="nav-button-left" style={{ position: 'absolute', left: 0 }}>
              {currentResultIndex > 0 && (
                <Button
                  onClick={handlePrevious}
                  style={{ display: 'flex', alignItems: 'center', gap: 8, minHeight: 44 }}
                >
← {t('demo.searchResults.previous')}
                </Button>
              )}
            </div>

            <div
              className="nav-guide-counter"
              style={{
                position: 'absolute',
                left: '50%',
                transform: 'translateX(-50%)',
                textAlign: 'center'
              }}
            >
              <Text type="secondary" style={{ fontSize: 'clamp(13px, 2.5vw, 14px)' }}>
    {t('demo.searchResults.guide')} {currentResultIndex + 1} {t('demo.searchResults.of')} {totalResults}
              </Text>
            </div>

            <div className="nav-button-right" style={{ position: 'absolute', right: 0 }}>
              {currentResultIndex < totalResults - 1 && (
                <Button
                  onClick={handleNext}
                  style={{ display: 'flex', alignItems: 'center', gap: 8, minHeight: 44 }}
                >
{t('demo.searchResults.next')} →
                </Button>
              )}
            </div>
          </div>

          {/* Guide ID badge - centered below navigation */}
          {currentGuideId && (
            <Tooltip title={t('demo.searchResults.clickToCopy')}>
              <div
                className="guide-id-badge"
                onClick={handleCopyGuideId}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: 'clamp(3px, 1vw, 3px) clamp(8px, 2vw, 8px)',
                  backgroundColor: '#f5f5f5',
                  borderRadius: 4,
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  fontSize: 'clamp(10px, 2vw, 11px)',
                  fontFamily: 'monospace',
                  color: '#999',
                  userSelect: 'none',
                  minHeight: 32
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#ececec';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#f5f5f5';
                }}
              >
                <span style={{ fontWeight: 500 }}>{t('demo.searchResults.guideId')}:</span>
                <span>{truncatedGuideId}</span>
                <CopyOutlined style={{ fontSize: 'clamp(10px, 2vw, 11px)' }} />
              </div>
            </Tooltip>
          )}
        </div>
      )}
    </div>
  );
});

SearchResults.displayName = 'SearchResults';